import React, { useState } from 'react'
import { useVersioning } from 'modules/versioning/ShareVersionContext'
import { setLightness } from 'polished'
import styled, { css } from 'styled-components'
import copy from 'copy-to-clipboard'
import { ButtonUnstyled } from 'components/Button'
import Dropdown from 'components/Dropdown'
import { useModalContext } from 'containers/ModalManager'
import { DeleteShareModal } from 'modules/shares/components/DeleteShareModal'
import { DeleteVersionModal } from './DeleteVersionModal'
import { useVersionStar } from '../hooks/useVersionStar'
import { useVersionUnstar } from '../hooks/useVersionUnstar'
import { ReactComponent as Ellipsis } from 'icons/ellipsis-16.svg'
import { VersionListItemFragment } from 'gqlTypes'
import { isNewStarMoreRecent } from '../utils'

const ActionsWrapper = styled.div`
  display: flex;
  flex-grow: 1;
  justify-content: flex-end;
`

const Options = styled.div<{ visible: boolean }>`
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transition: opacity
    ${({ theme }) =>
      `${theme.transitions.duration[2]} ${theme.transitions.timing.easeInOut}`};
`

const StyledEllipsis = styled(({ isActive, ...rest }) => (
  <Ellipsis {...rest} />
))`
  width: 16px;
  height: 16px;

  color: ${({ isActive, theme }) =>
    isActive ? setLightness(0.39, theme.colors.orange) : theme.colors.gray};

  :hover {
    color: ${({ isActive, theme }) =>
      isActive
        ? setLightness(0.29, theme.colors.orange)
        : theme.colors.text.dark.primary};
  }
`

export const containerStyles = css`
  &:hover ${Options} {
    opacity: 1;
  }
`

const ActionsButton = styled(ButtonUnstyled)`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4px;
`

export interface VersionItemOptionsProps {
  isActive: boolean
  to: string
  hovered: boolean
  documentUrl: string | null | undefined
  createdAt: string
  creatorName: string
  shareName: string
  shareID: string
  versionIdentifier: string
  totalCount: number
  kind: VersionListItemFragment['kind']
  latestStar?: VersionListItemFragment
  canEdit: boolean
  canStar: boolean
  downloadEnabled: boolean
}

export const ShareVersionItemOptions = ({
  isActive,
  to,
  hovered,
  documentUrl,
  createdAt,
  creatorName,
  shareName,
  shareID,
  versionIdentifier,
  totalCount,
  kind,
  latestStar,
  canEdit,
  canStar,
  downloadEnabled,
}: VersionItemOptionsProps) => {
  const [isDropdownOpen, setDropdownOpen] = useState(false)
  const { showModal } = useModalContext()
  const { isLibrary } = useVersioning()

  const isLatest = isNewStarMoreRecent(latestStar, createdAt)
  const [starVersion] = useVersionStar({
    versionIdentifier,
    isLibrary,
    isNewLatest: isLatest,
  })
  const [unstarVersion] = useVersionUnstar({
    versionIdentifier,
    isLibrary,
  })

  const handleCopyLink = (event: React.SyntheticEvent) => {
    // stop event propogation so that underlying elements wouldn't be clicked
    event.preventDefault()
    const link = `${window.location.origin}${to}`
    copy(link)
  }

  const handleDownloadLink = (event: React.SyntheticEvent) => {
    // stop event propogation so that underlying elements wouldn't be clicked
    event.preventDefault()
    if (!documentUrl) {
      // TODO: this should never happen
      // Capture this case once https://github.com/sketch-hq/Cloud/issues/1012
      // is available
      return
    }
    window.location.replace(documentUrl)
  }

  const DeleteVersion = () => (
    <Dropdown.Item
      intent="negative"
      onClick={event => {
        // stop event propogation so that underlying elements wouldn't be clicked
        event.preventDefault()
        showModal(DeleteVersionModal, {
          createdAt,
          creatorName,
          shareName,
          isActive,
          shareID,
          versionIdentifier,
        })
      }}>
      Delete&hellip;
    </Dropdown.Item>
  )

  const DeleteDocument = () => (
    <Dropdown.Item
      intent="negative"
      onClick={event => {
        // stop event propagation so that underlying elements wouldn't be clicked
        event.preventDefault()
        showModal(DeleteShareModal, {
          name: shareName,
          shareShortId: shareID,
        })
      }}>
      Delete Document&hellip;
    </Dropdown.Item>
  )

  const createOrDemoteStar = () => {
    if (!canEdit || !canStar) {
      return null
    }

    return kind === 'DRAFT' ? (
      <Dropdown.Item
        data-testid="create-star-update-dropdown"
        onClick={event => {
          // stop event propagation so that underlying elements wouldn't be clicked
          event.preventDefault()
          starVersion()
        }}>
        Star Update&hellip;
      </Dropdown.Item>
    ) : (
      <Dropdown.Item
        data-testid="demote-star-update-dropdown"
        onClick={event => {
          event.preventDefault()
          unstarVersion()
        }}>
        Unstar Update&hellip;
      </Dropdown.Item>
    )
  }

  return (
    <ActionsWrapper>
      <Options visible={hovered || isDropdownOpen}>
        <Dropdown
          toggle={
            <ActionsButton data-testid="version-item-options-button">
              <StyledEllipsis isActive={isActive} />
            </ActionsButton>
          }
          usePortal
          placement="bottom"
          onToggle={setDropdownOpen}
          data-testid="version-item-options">
          <Dropdown.Item onClick={handleCopyLink}>Copy Link</Dropdown.Item>
          {downloadEnabled && (
            <Dropdown.Item onClick={handleDownloadLink}>Download</Dropdown.Item>
          )}
          {createOrDemoteStar()}
          {canEdit && (
            <>
              <Dropdown.Divider />
              {totalCount > 1 ? <DeleteVersion /> : <DeleteDocument />}
            </>
          )}
        </Dropdown>
      </Options>
    </ActionsWrapper>
  )
}
